import { skipToken, useQuery } from '@tanstack/react-query';

import connection from '~services/connectionModule';

interface UseEmailDraftsQueryParams<T, E extends object> {
  enabled: boolean;
  errorMessage: string;
  fetchDrafts: (signal: AbortSignal) => Promise<{ drafts: T[] } & E>;
  queryKey: unknown[];
}

export default function useEmailDraftsQuery<T, E extends object = object>({
  enabled,
  errorMessage,
  fetchDrafts,
  queryKey,
}: UseEmailDraftsQueryParams<T, E>) {
  const { data, isError, isLoading, refetch } = useQuery({
    queryKey,
    queryFn: enabled
      ? async ({ signal }) => {
          try {
            return await fetchDrafts(signal);
          } catch (error) {
            connection.handleRequestError(error, errorMessage);
            throw error;
          }
        }
      : skipToken,
    // Each dialog open seeds its form from one fresh fetch: a cached preview could carry
    // stale recipients, and a refetch after the form is seeded would be ignored.
    gcTime: 0,
    retry: false,
    staleTime: Infinity,
  });

  return {
    data: data ?? null,
    drafts: data?.drafts ?? null,
    isError,
    isLoading,
    refetch,
  };
}
